import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ICONS from "../../../assets/Icons";
import CommonToast from "../../../components/_utils/CommonToast";
import { env } from "../../../env";
import axiosInstance from "../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  "X-Api-Key": sessionStorage.getItem("q2p_token"),
});

export const verifyBankAccount = createAsyncThunk(
  "sharedResources/verifyBankAccount",
  async (values, { rejectWithValue }) => {
    const { bank_account, ifsc_code, legal_name } = values;
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/verification/bank_account/verify`,
        { bank_account, ifsc_code, legal_name },
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      CommonToast({
        text: error.response?.data?.error,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(
        error.response?.data?.errors?.base
          ? error.response?.data?.errors?.base[0]
          : error.response?.data?.errors?.bank_account
            ? error.response?.data?.errors?.bank_account[0]
            : "An error occurred",
      );
    }
  },
);

export const getBankList = createAsyncThunk(
  "sharedResources/getBankList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/verification/bank/bank_list/`,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

export const getIFSCDetails = createAsyncThunk(
  "sharedResources/getIFSCDetails",
  async (values, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/verification/ifsc/` + values?.search_string,
        {
          headers: getHeaders(),
        },
      );

      if (response.data?.errors) {
        CommonToast({
          text: response.data.errors.base[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
        });
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPANDetails = createAsyncThunk(
  "sharedResources/getPANDetails",
  async (values, { rejectWithValue }) => {
    try {
      const pan = values?.pan_number || values?.search_string;
      const response = await axiosInstance.get(
        `${baseUrl}/verification/pan/${pan}`,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

export const verifyPAN = createAsyncThunk(
  "sharedResources/verifyPAN",
  async (values, { rejectWithValue }) => {
    const { pan_number, signal } = values || {
      pan_number: null,
    };
    const searchString = pan_number || values?.search_string;

    try {
      const response = await axiosInstance.post(
        `${baseUrl}/verification/pan/verify`,
        { pan: searchString },
        {
          headers: getHeaders(),
          signal,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

export const checkPANAadhaarLinkStatus = createAsyncThunk(
  "sharedResources/checkPANAadhaarLinkStatus",
  async (values, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/verification/pan/aadhaar-link-status`,
        {
          pan: values?.search_string,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

export const getCINDetails = createAsyncThunk(
  "sharedResources/getCINDetails",
  async (values, { rejectWithValue }) => {
    try {
      const cin = values?.cin_number || values?.search_string;
      const response = await axiosInstance.get(
        `${baseUrl}/verification/cin/${cin}`,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

export const verifyCIN = createAsyncThunk(
  "sharedResources/verifyCIN",
  async (values, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/verification/cin/verify`,
        {
          cin: values?.search_string,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.base[0] ||
        error?.response?.data?.message ||
        error.message;
      return rejectWithValue(errorMessage);
    }
  },
);

export const getDINDetails = createAsyncThunk(
  "sharedResources/getDINDetails",
  async (values, { rejectWithValue }) => {
    try {
      const din = values?.din_number || values?.search_string;

      const response = await axiosInstance.get(
        `${baseUrl}/verification/din/${din}`,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.base[0] ||
        error.response?.data?.message ||
        error.message;

      return rejectWithValue(errorMessage);
    }
  },
);

export const verifyDIN = createAsyncThunk(
  "sharedResources/verifyDIN",
  async (values, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/verification/din/verify`,
        {
          din: values?.search_string,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.base[0] ||
        error.response?.data?.message ||
        error.message;

      return rejectWithValue(errorMessage);
    }
  },
);

export const getGSTDetails = createAsyncThunk(
  "sharedResources/getGSTDetails",
  async (values, { rejectWithValue }) => {
    try {
      const gstin = values?.gst_number || values?.search_string;
      const response = await axiosInstance.get(
        `${baseUrl}/verification/gstin/${gstin}`,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

export const verifyGST = createAsyncThunk(
  "sharedResources/verifyGST",
  async (values, { rejectWithValue }) => {
    try {
      const gstin = values?.gstin || values?.search_string;
      const response = await axiosInstance.post(
        `${baseUrl}/verification/gstin/verify`,
        {
          gstin,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.base[0] ||
        error.response?.data?.message ||
        error.message;
      return rejectWithValue({
        status: false,
        message: errorMessage,
      });
    }
  },
);

export const panLookUp = createAsyncThunk(
  "sharedResources/panLookUp",
  async (values) => {
    const { pan_number, page } = values ? values : { pan_number: null };

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/accounts/beneficiary/lookup`,
        {
          headers: getHeaders(),
          params: {
            pan: pan_number,
            page_no: page,
            page_size: 100,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  },
);

export const bankLookUp = createAsyncThunk(
  "sharedResources/bankLookUp",
  async (values) => {
    const { bank_account_no, ifsc } = values
      ? values
      : { bank_account_no: null, ifsc: null };

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/accounts/beneficiary/lookup`,
        {
          headers: getHeaders(),
          params: {
            bank_account_no: bank_account_no,
            ifsc: ifsc,
            page_size: 100,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  },
);

export const getAllCategories = createAsyncThunk(
  "sharedResources/getAllCategories",
  async (values) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/transaction_categories`,
        {
          headers: getHeaders(),
          params: {
            search_string: values?.search_string,
            sort_by: values?.sort_by,
            category_type: values?.category_type,
            category_status: values?.category_status,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
);

export const uploadFile = createAsyncThunk(
  "sharedResources/uploadFile",
  async (values, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", values?.file);

      const response = await axiosInstance.post(
        `${baseUrl}/files/upload`,
        formData,
        {
          headers: {
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const uploadImage = createAsyncThunk(
  "sharedResources/uploadImage",
  async (values, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", values);
      const response = await axiosInstance.post(
        `${baseUrl}/images/upload`,
        formData,
        {
          headers: {
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getUpdateInformation = createAsyncThunk(
  "sharedResources/getUpdateInformation",
  async (values, { rejectWithValue }) => {
    try {
      const { resource_type, resource_id } = values || {};

      const response = await axiosInstance.get(
        `${baseUrl}/${resource_type}/${resource_id}/update_information`,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

const sharedResources = createSlice({
  name: "sharedResources",
  initialState: {
    verifyBankAccountData: false,
    verifyBankAccountLoading: null,
    verifyBankAccountError: null,
    verifiyBankAccountMessage: "",

    bankListLoading: false,
    bankListData: null,
    bankListError: null,

    ifscDetailsLoading: false,
    ifscDetailsData: null,
    ifscDetailsError: null,

    panDetailsData: null,
    panDetailsLoading: false,
    panDetailsError: null,

    panVerificationData: null,
    panVerificationLoading: false,
    panVerificationError: null,

    panAadhaarLinkStatusData: null,
    panAadhaarLinkStatusLoading: false,
    panAadhaarLinkStatusError: null,

    cinDetailsData: null,
    cinDetailsLoading: false,
    cinDetailsError: null,

    cinVerificationData: null,
    cinVerificationLoading: false,
    cinVerificationError: null,

    dinDetailsData: null,
    dinDetailsLoading: false,
    dinDetailsError: null,

    dinVerificationData: null,
    dinVerificationLoading: false,
    dinVerificationError: null,

    gstDetailsLoading: false,
    gstDetailsData: null,
    gstDetailsError: null,

    gstVerificationData: null,
    gstVerificationLoading: false,
    gstVerificationError: null,

    panLookupData: null,
    panLookupLoading: false,
    panLookupError: null,

    bankLookupData: null,
    bankLookupLoading: false,
    bankLookupError: null,

    categoriesData: {
      categories: [],
    },
    categoriesLoading: false,
    categoriesError: null,
    selectedCinData: null,
    selectedDinData: null,
    selectedPanData: null,
    selectedGstData: null,

    fileUploadLoading: null,
    fileUploadData: null,
    fileUploadError: null,

    imageUploadLoading: null,
    imageUploadData: null,
    imageUploadError: null,

    updateInformationLoading: false,
    updateInformationData: null,
    updateInformationError: null,
  },
  reducers: {
    selectedGst(state, action) {
      state.selectedGstData = action.payload;
    },
    resetSelectedGst(state) {
      state.selectedGstData = null;
    },

    resetVerifyGST(state) {
      state.gstDetailsLoading = false;
      state.gstDetailsData = null;
      state.gstDetailsError = null;
    },

    resetBankVerification(state) {
      state.verifyBankAccountLoading = null;
      state.verifyBankAccountData = null;
      state.verifyBankAccountError = null;
    },

    resetBankVerificationError(state) {
      state.verifyBankAccountError = null;
    },

    resetBankList(state) {
      state.bankListData = null;
      state.bankListLoading = null;
      state.bankListError = null;
    },

    resetIFSCDetails(state) {
      state.ifscDetailsData = null;
      state.ifscDetailsLoading = null;
      state.ifscDetailsError = null;
    },

    resetPANDetails(state) {
      state.panDetailsData = null;
      state.panDetailsLoading = null;
      state.panDetailsError = null;
    },

    resetPANVerification(state) {
      state.panVerificationData = null;
      state.panVerificationLoading = null;
      state.panVerificationError = null;
    },

    resetPANAadhaarLinkStatus(state) {
      state.panAadhaarLinkStatusData = null;
      state.panAadhaarLinkStatusLoading = null;
      state.panAadhaarLinkStatusError = null;
    },

    resetCINDetails(state) {
      state.cinDetailsData = null;
      state.cinDetailsLoading = null;
      state.cinDetailsError = null;
    },

    resetCINVerification(state) {
      state.cinVerificationData = null;
      state.cinVerificationLoading = null;
      state.cinVerificationError = null;
    },

    resetDINDetails(state) {
      state.dinDetailsData = null;
      state.dinDetailsLoading = null;
      state.dinDetailsError = null;
    },

    resetDINVerification(state) {
      state.dinVerificationData = null;
      state.dinVerificationLoading = null;
      state.dinVerificationError = null;
    },

    resetGSTDetails(state) {
      state.gstDetailsData = null;
      state.gstDetailsLoading = null;
      state.gstDetailsError = null;
    },

    resetGSTVerification(state) {
      state.gstVerificationData = null;
      state.gstVerificationLoading = null;
      state.gstVerificationError = null;
    },

    resetPanLookup(state) {
      state.panLookupData = null;
    },

    resetBankLookupData(state) {
      state.bankLookupData = null;
    },
    resetBankLookupError(state) {
      state.bankLookupError = null;
    },

    selectedCin(state, action) {
      state.selectedCinData = action.payload;
    },

    resetSelectedCin(state) {
      state.selectedCinData = null;
    },
    resetGetCinData(state) {
      state.cinDetailsLoading = null;
      state.cinDetailsError = null;
      state.cinDetailsData = null;
    },
    selectedDin(state, action) {
      state.selectedCinData = action.payload;
    },

    resetSelectedDin(state) {
      state.selectedCinData = null;
    },
    resetGetDinData(state) {
      state.dinDetailsLoading = null;
      state.dinDetailsError = null;
      state.dinDetailsData = null;
    },
    selectedPan(state, action) {
      state.selectedPanData = action.payload;
    },
    resetSelectedPan(state) {
      state.selectedPanData = null;
    },
    updateCategotyResponse(state, action) {
      state.categoriesData.categories = action.payload;
    },
    resetUpdateInformation(state) {
      state.updateInformationLoading = false;
      state.updateInformationData = null;
      state.updateInformationError = null;
    },
    resetCategoryData: (state) => {
      state.categoriesData = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(verifyBankAccount.pending, (state, action) => {
        state.verifyBankAccountLoading = true;
      })
      .addCase(verifyBankAccount.fulfilled, (state, action) => {
        state.verifyBankAccountLoading = false;
        state.verifyBankAccountError = false;
        state.verifyBankAccountData = action.payload;
      })
      .addCase(verifyBankAccount.rejected, (state, action) => {
        state.verifyBankAccountLoading = false;
        state.verifyBankAccountError = true;
        state.verifyBankAccountData = null;
        state.verifiyBankAccountMessage = action.payload;
      })
      .addCase(getBankList.pending, (state) => {
        state.bankListLoading = true;
      })
      .addCase(getBankList.fulfilled, (state, action) => {
        state.bankListLoading = false;
        state.bankListError = false;
        state.bankListData = action.payload;
      })
      .addCase(getBankList.rejected, (state) => {
        state.bankListLoading = false;
        state.bankListError = true;
        state.bankListData = null;
      })
      .addCase(getIFSCDetails.pending, (state) => {
        state.ifscDetailsLoading = true;
        state.ifscDetailsError = false;
      })
      .addCase(getIFSCDetails.fulfilled, (state, action) => {
        state.ifscDetailsLoading = false;
        state.ifscDetailsData = action.payload;
        state.ifscDetailsError = false;
      })
      .addCase(getIFSCDetails.rejected, (state, action) => {
        state.ifscDetailsLoading = false;
        state.ifscDetailsError = true;
      })
      .addCase(getPANDetails.pending, (state, action) => {
        state.panDetailsLoading = true;
      })
      .addCase(getPANDetails.fulfilled, (state, action) => {
        state.panDetailsLoading = false;
        state.panDetailsError = false;
        state.panDetailsData = action.payload;
      })
      .addCase(getPANDetails.rejected, (state, action) => {
        state.panDetailsLoading = false;
        state.panDetailsError = true;
        state.panDetailsData = null;
      })
      .addCase(verifyPAN.pending, (state, action) => {
        state.panVerificationLoading = true;
      })
      .addCase(verifyPAN.fulfilled, (state, action) => {
        state.panVerificationLoading = false;
        state.panVerificationError = false;
        state.panVerificationData = action.payload;
      })
      .addCase(verifyPAN.rejected, (state, action) => {
        state.panVerificationLoading = false;
        state.panVerificationError = true;
        state.panVerificationData = action.payload;
      })
      .addCase(checkPANAadhaarLinkStatus.pending, (state, action) => {
        state.panAadhaarLinkStatusLoading = true;
      })
      .addCase(checkPANAadhaarLinkStatus.fulfilled, (state, action) => {
        state.panAadhaarLinkStatusLoading = false;
        state.panAadhaarLinkStatusError = false;
        state.panAadhaarLinkStatusData = action.payload;
      })
      .addCase(checkPANAadhaarLinkStatus.rejected, (state, action) => {
        state.panAadhaarLinkStatusLoading = false;
        state.panAadhaarLinkStatusError = true;
        state.panAadhaarLinkStatusData = null;
      })
      .addCase(getCINDetails.pending, (state, action) => {
        state.cinDetailsLoading = true;
      })
      .addCase(getCINDetails.fulfilled, (state, action) => {
        state.cinDetailsLoading = false;
        state.cinDetailsError = false;
        state.cinDetailsData = action.payload;
      })
      .addCase(getCINDetails.rejected, (state, action) => {
        state.cinDetailsLoading = false;
        state.cinDetailsError = true;
        state.cinDetailsData = null;
      })
      .addCase(verifyCIN.pending, (state, action) => {
        state.cinVerificationLoading = true;
      })
      .addCase(verifyCIN.fulfilled, (state, action) => {
        state.cinVerificationLoading = false;
        state.cinVerificationError = false;
        state.cinVerificationData = action.payload;
      })
      .addCase(verifyCIN.rejected, (state, action) => {
        state.cinVerificationLoading = false;
        state.cinVerificationError = true;
        state.cinVerificationData = null;
      })
      .addCase(getDINDetails.pending, (state, action) => {
        state.dinDetailsLoading = true;
      })
      .addCase(getDINDetails.fulfilled, (state, action) => {
        state.dinDetailsLoading = false;
        state.dinDetailsError = false;
        state.dinDetailsData = action.payload;
      })
      .addCase(getDINDetails.rejected, (state, action) => {
        state.dinDetailsLoading = false;
        state.dinDetailsError = true;
        state.dinDetailsData = null;
      })
      .addCase(verifyDIN.pending, (state, action) => {
        state.dinVerificationLoading = true;
      })
      .addCase(verifyDIN.fulfilled, (state, action) => {
        state.dinVerificationLoading = false;
        state.dinVerificationError = false;
        state.dinVerificationData = action.payload;
      })
      .addCase(verifyDIN.rejected, (state, action) => {
        state.dinVerificationLoading = false;
        state.dinVerificationError = true;
        state.dinVerificationData = null;
      })
      .addCase(getGSTDetails.pending, (state, action) => {
        state.gstDetailsLoading = true;
      })
      .addCase(getGSTDetails.fulfilled, (state, action) => {
        state.gstDetailsLoading = false;
        state.gstDetailsError = false;
        state.gstDetailsData = action.payload;
      })
      .addCase(getGSTDetails.rejected, (state, action) => {
        state.gstDetailsLoading = false;
        state.gstDetailsError = true;
        state.gstDetailsData = null;
      })
      .addCase(verifyGST.pending, (state, action) => {
        state.gstVerificationLoading = true;
      })
      .addCase(verifyGST.fulfilled, (state, action) => {
        state.gstVerificationLoading = false;
        state.gstVerificationError = false;
        state.gstVerificationData = action.payload;
      })
      .addCase(verifyGST.rejected, (state, action) => {
        state.gstVerificationLoading = false;
        state.gstVerificationError = true;
        state.gstVerificationData = action.payload ?? false;
      })
      .addCase(panLookUp.pending, (state, action) => {
        state.panLookupLoading = true;
        state.panLookupError = false;
      })
      .addCase(panLookUp.fulfilled, (state, action) => {
        state.panLookupLoading = false;
        state.panLookupData = action.payload;
      })
      .addCase(panLookUp.rejected, (state, action) => {
        state.panLookupError = true;
        state.panLookupLoading = false;
        state.panLookupData = null;
      })
      .addCase(bankLookUp.pending, (state, action) => {
        state.bankLookupLoading = true;
        state.bankLookupError = false;
      })
      .addCase(bankLookUp.fulfilled, (state, action) => {
        state.bankLookupLoading = false;
        state.bankLookupData = action.payload;
      })
      .addCase(bankLookUp.rejected, (state, action) => {
        state.bankLookupError = true;
        state.bankLookupData = null;
        state.bankLookupLoading = false;
      })
      .addCase(getAllCategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(getAllCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = false;
        state.categoriesData = action.payload;
      })
      .addCase(getAllCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.error?.message || true;
      })
      .addCase(uploadFile.pending, (state) => {
        state.fileUploadLoading = true;
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.fileUploadLoading = false;
        state.fileUploadData = action.payload;
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.fileUploadLoading = false;
        state.fileUploadError = action.payload;
      })
      .addCase(uploadImage.pending, (state) => {
        state.imageUploadLoading = true;
      })
      .addCase(uploadImage.fulfilled, (state, action) => {
        state.imageUploadLoading = false;
        state.imageUploadData = action.payload;
      })
      .addCase(uploadImage.rejected, (state, action) => {
        state.imageUploadLoading = false;
        state.imageUploadError = action.payload;
      })
      .addCase(getUpdateInformation.pending, (state) => {
        state.updateInformationLoading = true;
        state.updateInformationError = null;
      })
      .addCase(getUpdateInformation.fulfilled, (state, action) => {
        state.updateInformationLoading = false;
        state.updateInformationError = null;
        state.updateInformationData = action.payload;
      })
      .addCase(getUpdateInformation.rejected, (state, action) => {
        state.updateInformationLoading = false;
        state.updateInformationData = null;
        state.updateInformationError = action.payload;
      });
  },
});

export const {
  resetBankVerification,
  resetBankVerificationError,
  resetBankList,
  resetIFSCDetails,

  resetPANDetails,
  resetPANVerification,
  resetPANAadhaarLinkStatus,

  resetCINDetails,
  resetCINVerification,

  resetDINDetails,
  resetDINVerification,

  resetGSTDetails,
  resetGSTVerification,

  resetPanLookup,
  resetBankLookupData,
  resetBankLookupError,

  selectedCin,
  resetSelectedCin,
  resetGetCinData,

  selectedDin,
  resetSelectedDin,
  resetGetDinData,

  selectedPanData,
  resetSelectedPan,

  selectedGst,
  resetSelectedGst,

  resetVerifyGST,
  updateCategotyResponse,

  resetUpdateInformation,
  resetCategoryData
} = sharedResources.actions;

export default sharedResources.reducer;
